import { getRectOfNodes } from 'reactflow';
import { getNodeDimensions, isNodeNotPositioned } from './graphLayoutPrimitives';
import { NodeType } from './types';
/**
 * Like {@link groupNodesIntoDisjointSubgraphs}, but also unions each new node with its `parentNode`
 * when that parent is also in `nodes`. Otherwise a freshly added subflow (no edges yet) is split
 * from its children and ELK lays gems out as root nodes while `parentNode` is later restored —
 * children render outside the VG box.
 */
export function partitionIntoConnectedComponents(nodes, edges) {
    if (nodes.length === 0)
        return [];
    const nodesById = new Map(nodes.map((node) => [node.id, node]));
    const nodeIdSet = new Set(nodes.map((node) => node.id));
    const parentOf = {};
    const findRoot = (id) => {
        if (parentOf[id] === undefined)
            parentOf[id] = id;
        if (parentOf[id] !== id)
            parentOf[id] = findRoot(parentOf[id]);
        return parentOf[id];
    };
    const merge = (idA, idB) => {
        const rootA = findRoot(idA);
        const rootB = findRoot(idB);
        if (rootA !== rootB)
            parentOf[rootB] = rootA;
    };
    nodes.forEach((node) => {
        parentOf[node.id] = node.id;
    });
    edges.forEach((edge) => {
        if (nodeIdSet.has(edge.source) && nodeIdSet.has(edge.target)) {
            merge(edge.source, edge.target);
        }
    });
    nodes.forEach((node) => {
        if (node.parentNode && nodeIdSet.has(node.parentNode)) {
            merge(node.id, node.parentNode);
        }
    });
    const componentsByRoot = new Map();
    nodes.forEach((node) => {
        const groupRoot = findRoot(node.id);
        const group = componentsByRoot.get(groupRoot) ?? [];
        group.push(nodesById.get(node.id));
        componentsByRoot.set(groupRoot, group);
    });
    return Array.from(componentsByRoot.values());
}
/**
 * Recompute `positionAbsolute` from `position` and `parentNode` chain. `nodeById` must contain every
 * ancestor (e.g. merged existing + new nodes). Used after applying a **canvas** offset to **root**
 * nodes only — children keep parent-relative `position` and must not receive the same delta.
 */
export function recomputeAbsolutePositions(nodes, nodeById = new Map(nodes.map((node) => [node.id, node]))) {
    const cache = new Map();
    const ancestors = new Set();
    const absoluteOf = (id) => {
        const cached = cache.get(id);
        if (cached)
            return cached;
        if (ancestors.has(id))
            return { x: 0, y: 0 };
        ancestors.add(id);
        const node = nodeById.get(id);
        if (!node) {
            ancestors.delete(id);
            return { x: 0, y: 0 };
        }
        let absolute;
        if (!node.parentNode || !nodeById.has(node.parentNode)) {
            absolute = { x: node.position.x, y: node.position.y };
        }
        else {
            const parentAbsolute = absoluteOf(node.parentNode);
            absolute = { x: parentAbsolute.x + node.position.x, y: parentAbsolute.y + node.position.y };
        }
        cache.set(id, absolute);
        ancestors.delete(id);
        return absolute;
    };
    nodes.forEach((node) => {
        node.positionAbsolute = absoluteOf(node.id);
    });
}
/**
 * Splits nodes into already-positioned vs unpositioned (new) sets for preserve-layout,
 * and records each positioned node's coordinates for later collision resolution.
 */
export function partitionNodesForPreserveLayout(nodes) {
    const existingNodes = [];
    const newNodes = [];
    const originalNodesPositions = new Map();
    nodes.forEach((node) => {
        if (isNodeNotPositioned(node)) {
            newNodes.push(node);
        }
        else {
            existingNodes.push(node);
            originalNodesPositions.set(node.id, node.position);
        }
    });
    return { existingNodes, newNodes, originalNodesPositions };
}
/**
 * Calculates the absolute position of a node in the graph by recursively
 * accumulating the positions of all ancestor nodes.
 *
 * Since ELK layout provides positions relative to a node's parent container,
 * this function traverses up the parent hierarchy to compute the final
 * absolute coordinates in the graph's coordinate system.
 *
 * @param nodeId - The ID of the node to get the absolute position for
 * @param nodeElkMap - Map of node IDs to their ELK node data (including parent references)
 * @param nodePositionMap - Map of node IDs to their relative positions from ELK layout
 * @returns The absolute XY position of the node, or undefined if the node is not found
 */
export function getNodeAbsolutePosition(nodeId, nodeElkMap, nodePositionMap) {
    const node = nodeElkMap.get(nodeId);
    if (!node) {
        return undefined;
    }
    const nodePosition = nodePositionMap.get(nodeId);
    if (!nodePosition) {
        return undefined;
    }
    let nodeX = nodePosition.x;
    let nodeY = nodePosition.y;
    if (node.parentId) {
        const nodeParentPosition = getNodeAbsolutePosition(node.parentId, nodeElkMap, nodePositionMap);
        if (nodeParentPosition) {
            nodeX += nodeParentPosition.x;
            nodeY += nodeParentPosition.y;
        }
    }
    return { x: nodeX, y: nodeY };
}
export function buildNodeIdToGraphNodeMap(node, nodeIdToGraphNodeMap = new Map()) {
    if (!node.children) {
        return nodeIdToGraphNodeMap;
    }
    node.children.forEach((child) => {
        const nodeValue = { ...child, parentId: node.id };
        nodeIdToGraphNodeMap.set(child.id, nodeValue);
        if (child.children) {
            buildNodeIdToGraphNodeMap(child, nodeIdToGraphNodeMap);
        }
    });
    return nodeIdToGraphNodeMap;
}
/**
 * Returns the bounding rect of all already-positioned nodes (existing + laid-out new).
 * Used to position completely disconnected subgraphs at the bottom of the canvas.
 */
export function getCurrentGraphBounds(adjustedExistingNodes, layoutedNewNodes) {
    if (adjustedExistingNodes.length === 0 && layoutedNewNodes.length === 0) {
        return { x: 0, y: 0, width: 0, height: 0 };
    }
    return getRectOfNodes([...adjustedExistingNodes, ...layoutedNewNodes]);
}
/**
 * Reorders `targetHandles` and `sourceHandles` on the existing nodes that gained a new
 * connection to a just-inserted gem so that ports appear top-to-bottom according to the
 * vertical position of their connected neighbors.
 *
 * Only nodes in `affectedNodeIds` are touched; subflow and comment nodes are skipped.
 * For subflow children, `positionAbsolute` (or parent + relative position) is used so that
 * Y comparisons are always in canvas space regardless of nesting.
 */
export function updatePortSequence(allNodes, edges, affectedNodeIds) {
    const nodeIdMap = new Map(allNodes.map((n) => [n.id, n]));
    const sourceHandleToTargetNode = new Map();
    const targetHandleToSourceNode = new Map();
    edges.forEach((edge) => {
        const srcNode = nodeIdMap.get(edge.source);
        const tgtNode = nodeIdMap.get(edge.target);
        if (edge.sourceHandle && tgtNode)
            sourceHandleToTargetNode.set(edge.sourceHandle, tgtNode);
        if (edge.targetHandle && srcNode)
            targetHandleToSourceNode.set(edge.targetHandle, srcNode);
    });
    const getCanvasY = (node) => {
        if (node.positionAbsolute)
            return node.positionAbsolute.y;
        if (node.parentNode) {
            const parent = nodeIdMap.get(node.parentNode);
            return parent ? parent.position.y + node.position.y : node.position.y;
        }
        return node.position.y;
    };
    const getCanvasX = (node) => {
        if (node.positionAbsolute)
            return node.positionAbsolute.x;
        if (node.parentNode) {
            const parent = nodeIdMap.get(node.parentNode);
            return parent ? parent.position.x + node.position.x : node.position.x;
        }
        return node.position.x;
    };
    /**
     * Projects the straight-line edge from `connectedNode` to `currentNode` onto
     * the current node's left or right edge and returns the Y where it arrives.
     * Sorting ports by this value produces a crossing-free handle ordering that
     * accounts for both X and Y distance, not just absolute Y of the peer node.
     */
    const getEdgeArrivalY = (connectedNode, currentNode, side) => {
        if (!connectedNode)
            return getCanvasY(currentNode);
        const cx = getCanvasX(connectedNode);
        const cy = getCanvasY(connectedNode);
        const nx = getCanvasX(currentNode);
        const ny = getCanvasY(currentNode);
        const dx = cx - nx;
        if (Math.abs(dx) < 1)
            return cy;
        const { w } = getNodeDimensions(currentNode);
        const edgeX = side === 'left' ? nx - w / 2 : nx + w / 2;
        return cy + ((edgeX - cx) / (nx - cx)) * (ny - cy);
    };
    affectedNodeIds.forEach((nodeId) => {
        const node = nodeIdMap.get(nodeId);
        if (!node)
            return;
        if (node.type === NodeType.subFlow || node.type === NodeType.comment)
            return;
        const { targetHandles, sourceHandles } = node.data;
        let nextTargets = targetHandles;
        let nextSources = sourceHandles;
        let orderChanged = false;
        if (targetHandles.length > 1) {
            const sortedTargets = [...targetHandles].sort((a, b) => getEdgeArrivalY(targetHandleToSourceNode.get(a.id), node, 'left') -
                getEdgeArrivalY(targetHandleToSourceNode.get(b.id), node, 'left'));
            if (!targetHandles.every((h, i) => h.id === sortedTargets[i].id)) {
                nextTargets = sortedTargets;
                orderChanged = true;
            }
        }
        if (sourceHandles.length > 1) {
            const sortedSources = [...sourceHandles].sort((a, b) => getEdgeArrivalY(sourceHandleToTargetNode.get(a.id), node, 'right') -
                getEdgeArrivalY(sourceHandleToTargetNode.get(b.id), node, 'right'));
            if (!sourceHandles.every((h, i) => h.id === sortedSources[i].id)) {
                nextSources = sortedSources;
                orderChanged = true;
            }
        }
        if (!orderChanged)
            return;
        // find the index of the node in the allNodes array and update the node with the new source and target handles
        const index = allNodes.findIndex((n) => n.id === nodeId);
        if (index !== -1) {
            const updated = {
                ...node,
                data: { ...node.data, targetHandles: nextTargets, sourceHandles: nextSources }
            };
            allNodes[index] = updated;
            nodeIdMap.set(nodeId, updated);
        }
    });
    return allNodes;
}
